import { zodResolver } from '@hookform/resolvers/zod';
import { curry, equals, not } from 'ramda';
import React, { FC, useContext, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import Checkbox from '@ui/forms/Checkbox';
import TextInput from '@ui/forms/TextInput';

import BackAndNextNav from '@ui/molecules/BackAndNextNav';
import CenteredGridItem, { ICenteredGridItemProps } from '@ui/molecules/CenteredGridItem';

import spaced from '@ui/styled/Spaced';

import useResetField from '@hooks/useResetField';

import { commonPlaceholders } from '@utils/placeholder';

import FlowContext from '@providers/FlowContextProvider';

import { schema } from './MemberTestosteroneGoalsStep.data';
import { MemberTestosteroneGoalsStepProps } from './MemberTestosteroneGoalsStep.page';

const SpacedGridItem = spaced<ICenteredGridItemProps>(CenteredGridItem);

export interface IMemberTestosteroneGoalsPageContent {
	pageHeader: string;
	pageInfo: string;
	hrtGoalsEmotionalBenefits: string;
	hrtGoalsGenderEuphoria: string;
	hrtGoalsDeepenVoice: string;
	hrtGoalsImproveLibido: string;
	hrtGoalsBottomGrowth: string;
	hrtGoalsRedistributeFat: string;
	hrtGoalsMuscleStrength: string;
	hrtGoalsChestChange: string;
	hrtGoalsBodyHair: string;
	hrtGoalsFacialHair: string;
	hrtGoalsOther: string;
}

const MemberTestosteroneGoalsStep: FC<MemberTestosteroneGoalsStepProps> = ({
	pageContent,
	stepKey,
}) => {
	const [submitting, setSubmitting] = useState(false);

	const { existingFormData, handleFormSubmission, handleNavBack, hideNavBack } =
		useContext(FlowContext);

	const {
		pageHeader,
		pageInfo,
		hrtGoalsEmotionalBenefits,
		hrtGoalsGenderEuphoria,
		hrtGoalsDeepenVoice,
		hrtGoalsImproveLibido,
		hrtGoalsBottomGrowth,
		hrtGoalsRedistributeFat,
		hrtGoalsMuscleStrength,
		hrtGoalsChestChange,
		hrtGoalsBodyHair,
		hrtGoalsFacialHair,
		hrtGoalsOther,
	} = pageContent;

	type Schema = Partial<z.input<typeof schema>>;

	const form = useForm<Schema>({
		defaultValues: {
			...(existingFormData as unknown as Schema),
		},
		resolver: zodResolver(schema),
		mode: 'onChange',
	});
	const {
		handleSubmit,
		watch,
		register,
		setValue,
		formState: { errors },
	} = form;

	const hasOtherHrtGoals = !!watch('hrtGoalsOther');

	useResetField<boolean | string>({
		fieldValue: hasOtherHrtGoals,
		condition: curry((v) => not(equals(true as boolean)(v))),
		fields: ['hrtGoalsOtherText'],
		setValue,
	});

	const onSubmit = async (data: Record<string, unknown>) => {
		setSubmitting(true);
		await handleFormSubmission(data, stepKey);
		setSubmitting(false);
	};

	return (
		<SpacedGridItem marginBottom="lgxl">
			<form onSubmit={handleSubmit(onSubmit)}>
				<FormProvider {...form}>
					<FlexColumn gap="lg">
						<FlexColumn gap="sm">
							<HeadingText>{pageHeader}</HeadingText>
							<WYSIWYGContent dangerouslySetInnerHTML={{ __html: pageInfo }} />
						</FlexColumn>
						<Checkbox
							label={hrtGoalsEmotionalBenefits}
							{...register('hrtGoalsEmotionalBenefits')}
						/>
						<Checkbox label={hrtGoalsGenderEuphoria} {...register('hrtGoalsGenderEuphoria')} />
						<Checkbox label={hrtGoalsDeepenVoice} {...register('hrtGoalsDeepenVoice')} />
						<Checkbox label={hrtGoalsImproveLibido} {...register('hrtGoalsImproveLibido')} />
						<Checkbox label={hrtGoalsBottomGrowth} {...register('hrtGoalsBottomGrowth')} />
						<Checkbox label={hrtGoalsRedistributeFat} {...register('hrtGoalsRedistributeFat')} />
						<Checkbox label={hrtGoalsMuscleStrength} {...register('hrtGoalsMuscleStrength')} />
						<Checkbox label={hrtGoalsChestChange} {...register('hrtGoalsChestChange')} />
						<Checkbox label={hrtGoalsBodyHair} {...register('hrtGoalsBodyHair')} />
						<Checkbox label={hrtGoalsFacialHair} {...register('hrtGoalsFacialHair')} />
						<Checkbox label={hrtGoalsOther} {...register('hrtGoalsOther')} />
						{hasOtherHrtGoals && (
							<TextInput
								{...register('hrtGoalsOtherText')}
								placeholder={commonPlaceholders.more}
								name="hrtGoalsOtherText"
								error={!!errors?.hrtGoalsOtherText}
								errorLabel={errors?.hrtGoalsOtherText?.message ?? ''}
							/>
						)}
					</FlexColumn>
					<BackAndNextNav
						marginTop="xxl"
						marginBottom="xxxl"
						submitLabel="Next"
						backOnClick={() => handleNavBack(stepKey)}
						submitting={submitting}
					/>
				</FormProvider>
			</form>
		</SpacedGridItem>
	);
};

export default MemberTestosteroneGoalsStep;
